<template>
  <!-- Step 5, the last one. Mirrors the mobile settings step (iOS's
       OnBoardingAppSettingsViewController, Android's OnboardingSettingsFragment):
       units, coordinates, hiking pace and language.

       Mobile also offers a temperature toggle. Web deliberately doesn't: there
       is no temperature column on users and no GraphQL argument for it, because
       both apps keep that preference on the device. A web control that silently
       forgot would be worse than its absence.

       Every field is optional — each has a server-side default — so Finish is
       always available, and a list Rails didn't publish simply isn't shown. -->
  <div>
    <OnBoardingStepIndicator :current="6" />

    <div class="back-link mb-3" @click="backTapped">
      <span class="material-icons">arrow_left</span>
      <span>{{ $t('on_boarding.profile_steps.back') }}</span>
    </div>

    <h2 class="auth-heading">{{ $t('on_boarding.profile_settings_page.title') }}</h2>
    <p class="auth-sub">{{ $t('on_boarding.profile_settings_page.subtitle') }}</p>

    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      {{ errorMessage }}
    </div>

    <form @submit.prevent="submit">
      <div v-if="options.measurement.length" class="pro-field settings-field">
        <label class="pro-field__label" for="settingsMeasurement">
          {{ $t('on_boarding.profile_settings_page.measurement') }}
        </label>
        <select id="settingsMeasurement" v-model="measurement" class="form-control pro-field__input">
          <option :value="null">{{ $t('on_boarding.profile_settings_page.not_set') }}</option>
          <option v-for="option in options.measurement" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>

      <div v-if="options.gridReference.length" class="pro-field settings-field">
        <label class="pro-field__label" for="settingsGridReference">
          {{ $t('on_boarding.profile_settings_page.grid_reference') }}
        </label>
        <select id="settingsGridReference" v-model="gridReference" class="form-control pro-field__input">
          <option :value="null">{{ $t('on_boarding.profile_settings_page.not_set') }}</option>
          <option v-for="option in options.gridReference" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>

      <div v-if="options.hikeTime.length" class="pro-field settings-field">
        <label class="pro-field__label" for="settingsHikeTime">
          {{ $t('on_boarding.profile_settings_page.hike_time') }}
        </label>
        <select id="settingsHikeTime" v-model="hikeTime" class="form-control pro-field__input">
          <option :value="null">{{ $t('on_boarding.profile_settings_page.not_set') }}</option>
          <option v-for="option in options.hikeTime" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>

      <div v-if="locales.length > 1" class="pro-field settings-field settings-field--last">
        <label class="pro-field__label" for="settingsLanguage">
          {{ $t('on_boarding.profile_settings_page.language') }}
        </label>
        <!-- Stored with everything else and applied on the next page load
             rather than immediately: re-rendering the app mid-wizard would
             swap the copy out from under the step the user is standing on. -->
        <select id="settingsLanguage" v-model="locale" class="form-control pro-field__input">
          <option v-for="option in locales" :key="option.code" :value="option.code">
            {{ option.name }}
          </option>
        </select>
      </div>

      <button type="submit" class="btn pro-btn pro-btn--accent w-100" :disabled="isLoading">
        <template v-if="isLoading">
          <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        </template>
        <template v-else>
          <span class="material-icons">arrow_forward</span>
        </template>
        <span>{{ $t('on_boarding.profile_settings_page.continue') }}</span>
      </button>
    </form>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import OnBoardingStepIndicator from './OnBoardingStepIndicator.vue';
import { parseLocales, type SupportedLocale } from '../../../i18n/supportedLocales';
import {
  defaultSettingsForRegion,
  effectiveRegionCountry,
  isAllowedPreference,
  signupRegion,
  type PreferenceOption,
  type PreferenceOptions,
  type SignupCountry,
} from '../../../utils/helpers/signupProfile';

export default defineComponent({
  name: 'OnBoardingProfileSettingsPage',
  components: { OnBoardingStepIndicator },
  data() {
    const state = this.$store.state.onBoarding;
    // Prefill from the region, mirroring the mobile apps' locale defaults —
    // except the region comes from the country the user picked two steps back
    // rather than a browser setting, which is better evidence.
    //
    // These are then saved like any other answer. That is deliberate and
    // differs from the account-settings page, which keeps a blank option so
    // editing an unrelated field can't silently record units the user never
    // saw: writing measurement_preference stamps a timestamp the phones read as
    // a real choice. Here the field is on screen and labelled when Finish is
    // pressed, so confirming it is a choice.
    const chosen = (state.countries || []).find(
      (entry: SignupCountry) => String(entry.id) === String(state.profileCountryId),
    ) || null;
    // A sub-region (a US state, a Canadian province) stores its own code, so
    // the region comes from its parent row.
    const country = effectiveRegionCountry(chosen, state.countries);
    const region = signupRegion(country, typeof navigator !== 'undefined' ? navigator.language : null);
    const regionDefaults = defaultSettingsForRegion(region);
    // Only preselect a default the server actually offers. A value missing from
    // the list leaves the <select> on selectedIndex -1, which renders as an
    // empty box — worse than showing "Use the default".
    const options: PreferenceOptions = state.preferenceOptions;
    const offered = (list: PreferenceOption[], value: string) => (
      isAllowedPreference(list, value) ? value : null
    );
    return {
      measurement: (state.profileMeasurement || offered(options.measurement, regionDefaults.measurementPreference)) as string | null,
      gridReference: (state.profileGridReference || offered(options.gridReference, regionDefaults.gridReference)) as string | null,
      hikeTime: state.profileHikeTime as string | null,
      // Defaults to the language the page is already being read in.
      locale: (state.profileLocale || (window as any).hiikerLocale || 'en') as string,
      locales: parseLocales((window as any).hiikerLocales) as SupportedLocale[],
    };
  },
  computed: {
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    isLoading(): boolean {
      return this.$store.state.onBoarding.isLoading;
    },
    options(): PreferenceOptions {
      return this.$store.state.onBoarding.preferenceOptions;
    },
  },
  methods: {
    submit() {
      this.$store.commit('onBoarding/setProfileMeasurement', this.measurement);
      this.$store.commit('onBoarding/setProfileGridReference', this.gridReference);
      this.$store.commit('onBoarding/setProfileHikeTime', this.hikeTime);
      this.$store.commit('onBoarding/setProfileLocale', this.locale);
      this.$store.dispatch('onBoarding/submitProfileSettings');
    },
    backTapped() {
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/popPage');
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
  },
});
</script>

<style scoped>
.auth-heading {
  font-family: 'Montserrat', Helvetica, Arial, sans-serif;
  font-size: clamp(1.4rem, 2.4vw, 1.75rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: #fff;
  margin: 0 0 0.35rem;
}
.auth-sub {
  color: var(--pro-text-muted);
  font-size: 0.9rem;
  margin: 0 0 0.9rem;
}
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--pro-text-muted);
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
}
.back-link:hover { color: #fff; }

/* The v2 field styling targets inputs; selects need the same treatment plus a
   chevron, since appearance:none removes the native one. */
select.pro-field__input {
  appearance: none;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23a1a4ad'%3E%3Cpath d='M4.5 6l3.5 4 3.5-4z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 36px;
}
.settings-field { margin-bottom: 0.5rem; }
.settings-field--last { margin-bottom: 0.9rem; }

@media (max-height: 760px) {
  .auth-sub { margin-bottom: 0.6rem; }
  .settings-field { margin-bottom: 0.35rem; }
  .settings-field--last { margin-bottom: 0.6rem; }
}

select.pro-field__input option {
  background-color: #1a1c22;
  color: #fff;
}
</style>
